/**
 * Offline Service
 * Manages offline data caching, credential storage, and sync
 */

import offlineDb from './offlineDb';
import crypto from './crypto';
import api from './api';
import { format, subDays, addDays } from 'date-fns';

const { STORES } = offlineDb;

// Sync configuration
const SYNC_CONFIG = {
  PAST_DAYS: 30,   // Cache last 30 days
  FUTURE_DAYS: 90, // Cache next 90 days
  RETRY_LIMIT: 3
};

/**
 * Check if browser is online
 */
export const isOnline = () => navigator.onLine;

/**
 * Simple hash function (djb2) — no Web Crypto dependency
 */
const simpleHash = (str) => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return 'h' + Math.abs(hash).toString(36);
};

/**
 * Store offline credentials after successful login
 */
export const storeOfflineCredentials = async (email, password, user) => {
  const emailLower = email.toLowerCase();

  // ── Primary: localStorage (always reliable, uses simple hash) ──
  try {
    let hashHex;
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(emailLower + ':' + password);
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
      hashHex = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
      // Fallback: simple hash when Web Crypto unavailable
      hashHex = simpleHash(emailLower + ':' + password);
    }
    localStorage.setItem(`offline_cred_${emailLower}`, JSON.stringify({
      hash: hashHex,
      user: user,
      storedAt: new Date().toISOString(),
    }));
  } catch (lsErr) {
    console.error('Failed to store offline credentials in localStorage:', lsErr);
  }

  // ── Secondary: IndexedDB + Web Crypto (encrypted, best-effort) ──
  try {
    const verifier = await crypto.createPasswordVerifier(email, password);
    const encryptedUser = await crypto.encryptUserData(password, verifier.salt, user);
    await offlineDb.put(STORES.CREDENTIALS, {
      email,
      ...verifier,
      encryptedUser,
      lastLogin: new Date().toISOString()
    });
  } catch (error) {
    console.warn('IndexedDB/Crypto credential storage failed (localStorage fallback used):', error);
  }

  return true;
};

/**
 * Check if user has offline credentials stored
 */
export const hasOfflineCredentials = async (email) => {
  try {
    // Check localStorage first (fast)
    const lsKey = `offline_cred_${email.toLowerCase()}`;
    if (localStorage.getItem(lsKey)) return true;
    // Fall back to IndexedDB
    const stored = await offlineDb.get(STORES.CREDENTIALS, email);
    return !!stored;
  } catch (error) {
    return false;
  }
};

/**
 * Attempt offline login
 */
export const attemptOfflineLogin = async (email, password) => {
  const emailLower = email.toLowerCase();

  // ── Try 1: localStorage (fast, reliable) ──
  try {
    const lsKey = `offline_cred_${emailLower}`;
    const raw = localStorage.getItem(lsKey);
    if (raw) {
      const cred = JSON.parse(raw);
      let hashHex;
      try {
        const encoder = new TextEncoder();
        const data = encoder.encode(emailLower + ':' + password);
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
        hashHex = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
      } catch {
        hashHex = simpleHash(emailLower + ':' + password);
      }
      if (hashHex === cred.hash) {
        return { success: true, user: cred.user, isOffline: true };
      } else {
        return { success: false, error: 'Invalid credentials' };
      }
    }
  } catch (lsErr) {
    console.warn('localStorage offline login failed, trying IndexedDB:', lsErr);
  }

  // ── Try 2: IndexedDB + Web Crypto (encrypted) ──
  try {
    const stored = await offlineDb.get(STORES.CREDENTIALS, email);
    
    if (!stored) {
      return {
        success: false,
        error: 'First login requires internet connection.'
      };
    }
    
    // Verify password
    const isValid = await crypto.verifyPassword(email, password, stored);
    
    if (!isValid) {
      return {
        success: false,
        error: 'Invalid credentials'
      };
    }
    
    // Decrypt user data
    const user = await crypto.decryptUserData(password, stored.salt, stored.encryptedUser);
    
    if (!user) {
      return {
        success: false,
        error: 'Failed to decrypt stored credentials'
      };
    }
    
    return {
      success: true,
      user,
      isOffline: true
    };
  } catch (error) {
    console.error('Offline login error:', error);
    return {
      success: false,
      error: 'Offline login failed. Please connect to the internet to sign in.'
    };
  }
};

/**
 * Clear offline credentials for logout
 */
export const clearOfflineCredentials = async (email = null) => {
  try {
    if (email) {
      await offlineDb.remove(STORES.CREDENTIALS, email);
      localStorage.removeItem(`offline_cred_${email.toLowerCase()}`);
    } else {
      await offlineDb.clear(STORES.CREDENTIALS);
      // Clear all localStorage offline credential entries
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('offline_cred_')) keysToRemove.push(key);
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    }
    // Also clear cached data
    await offlineDb.clearAllData();
    return true;
  } catch (error) {
    console.error('Failed to clear offline credentials:', error);
    return false;
  }
};

/**
 * Cache schedule data for auditor
 */
export const cacheScheduleData = async (auditorId) => {
  try {
    const today = new Date();
    const startDate = format(subDays(today, SYNC_CONFIG.PAST_DAYS), 'yyyy-MM-dd');
    const endDate = format(addDays(today, SYNC_CONFIG.FUTURE_DAYS), 'yyyy-MM-dd');
    
    // Fetch jobs for auditor (max limit is 500)
    const jobs = await api.get('/jobs', {
      params: {
        auditor_id: auditorId,
        start_date: startDate,
        end_date: endDate,
        limit: 500
      }
    }).then(r => r.data);
    
    // Store jobs with auditor IDs for indexing
    const jobsWithIndex = jobs.map(job => ({
      ...job,
      auditor_ids: job.auditors?.map(a => a.id) || []
    }));
    
    await offlineDb.putAll(STORES.JOBS, jobsWithIndex);
    
    // Extract and store unique sites
    const sites = [...new Map(jobs.map(j => j.site).filter(Boolean).map(s => [s.id, s])).values()];
    await offlineDb.putAll(STORES.SITES, sites);
    
    // Extract and store unique clients
    const clients = [];
    jobs.forEach(job => {
      if (job.site?.parent_client) clients.push(job.site.parent_client);
      if (job.site?.independent_client) clients.push(job.site.independent_client);
    });
    const uniqueClients = [...new Map(clients.filter(Boolean).map(c => [c.id, c])).values()];
    await offlineDb.putAll(STORES.CLIENTS, uniqueClients);
    
    // Fetch and store services
    try {
      const services = await api.get('/services', { params: { is_active: true } }).then(r => r.data);
      await offlineDb.putAll(STORES.SERVICES, services);
    } catch (e) {
      console.warn('Could not cache services:', e);
    }
    
    // Update sync metadata
    await offlineDb.put(STORES.META, {
      key: 'lastSync',
      timestamp: new Date().toISOString(),
      auditorId
    });
    
    return {
      success: true,
      jobCount: jobs.length,
      siteCount: sites.length,
      clientCount: uniqueClients.length
    };
  } catch (error) {
    console.error('Failed to cache schedule data:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get cached jobs for auditor
 */
export const getCachedJobs = async () => {
  try {
    return await offlineDb.getAll(STORES.JOBS);
  } catch (error) {
    console.error('Failed to get cached jobs:', error);
    return [];
  }
};

/**
 * Get cached sites
 */
export const getCachedSites = async () => {
  try {
    return await offlineDb.getAll(STORES.SITES);
  } catch (error) {
    console.error('Failed to get cached sites:', error);
    return [];
  }
};

/**
 * Get cached clients
 */
export const getCachedClients = async () => {
  try {
    return await offlineDb.getAll(STORES.CLIENTS);
  } catch (error) {
    console.error('Failed to get cached clients:', error);
    return [];
  }
};

/**
 * Get cached services
 */
export const getCachedServices = async () => {
  try {
    return await offlineDb.getAll(STORES.SERVICES);
  } catch (error) {
    console.error('Failed to get cached services:', error);
    return [];
  }
};

/**
 * Get last sync info
 */
export const getLastSyncInfo = async () => {
  try {
    return await offlineDb.get(STORES.META, 'lastSync');
  } catch (error) {
    return null;
  }
};

/**
 * Add note to outbox for offline sync
 */
export const addNoteOffline = async (jobId, note) => {
  try {
    await offlineDb.addToOutbox({
      type: 'UPDATE_JOB_NOTES',
      jobId,
      data: { notes: note },
      entityType: 'job'
    });
    
    // Update local cache
    const job = await offlineDb.get(STORES.JOBS, jobId);
    if (job) {
      await offlineDb.put(STORES.JOBS, {
        ...job,
        notes: note,
        _pendingSync: true
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Failed to add note offline:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get outbox items
 */
export const getOutboxItems = async () => {
  try {
    const allItems = await offlineDb.getAll(STORES.OUTBOX);
    return allItems.sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    );
  } catch (error) {
    console.error('Failed to get outbox items:', error);
    return [];
  }
};

/**
 * Get pending outbox count
 */
export const getPendingOutboxCount = async () => {
  try {
    const pending = await offlineDb.getOutboxByStatus('pending');
    return pending.length;
  } catch (error) {
    return 0;
  }
};

/**
 * Process single outbox item
 */
const processOutboxItem = async (item) => {
  try {
    switch (item.type) {
      case 'UPDATE_JOB_NOTES':
        await api.put(`/jobs/${item.jobId}`, item.data);
        break;
      default:
        throw new Error(`Unknown outbox type: ${item.type}`);
    }
    
    // Mark as synced
    await offlineDb.updateOutboxStatus(item.id, 'synced');
    
    // Update local cache to remove pending flag
    if (item.entityType === 'job') {
      const job = await offlineDb.get(STORES.JOBS, item.jobId);
      if (job) {
        const { _pendingSync, ...cleanJob } = job;
        await offlineDb.put(STORES.JOBS, cleanJob);
      }
    }
    
    return { success: true };
  } catch (error) {
    // Mark as error if exceeded retry limit
    if ((item.attempts || 0) >= SYNC_CONFIG.RETRY_LIMIT) {
      await offlineDb.updateOutboxStatus(item.id, 'error', error.message);
    } else {
      await offlineDb.updateOutboxStatus(item.id, 'pending', error.message);
    }
    return { success: false, error: error.message };
  }
};

/**
 * Sync all pending outbox items
 */
export const syncOutbox = async () => {
  if (!isOnline()) {
    return { success: false, error: 'No internet connection' };
  }
  
  try {
    const pendingItems = await offlineDb.getOutboxByStatus('pending');
    const results = {
      total: pendingItems.length,
      synced: 0,
      failed: 0
    };
    
    for (const item of pendingItems) {
      const result = await processOutboxItem(item);
      if (result.success) {
        results.synced++;
      } else {
        results.failed++;
      }
    }
    
    return { success: true, results };
  } catch (error) {
    console.error('Failed to sync outbox:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove synced items from outbox
 */
export const clearSyncedOutbox = async () => {
  try {
    const synced = await offlineDb.getOutboxByStatus('synced');
    for (const item of synced) {
      await offlineDb.remove(STORES.OUTBOX, item.id);
    }
    return { success: true, removed: synced.length };
  } catch (error) {
    console.error('Failed to clear synced outbox:', error);
    return { success: false, error: error.message };
  }
};

export const offlineService = {
  isOnline,
  storeOfflineCredentials,
  hasOfflineCredentials,
  attemptOfflineLogin,
  clearOfflineCredentials,
  cacheScheduleData,
  getCachedJobs,
  getCachedSites,
  getCachedClients,
  getCachedServices,
  getLastSyncInfo,
  addNoteOffline,
  getOutboxItems,
  getPendingOutboxCount,
  syncOutbox,
  clearSyncedOutbox,
  SYNC_CONFIG
};

export default offlineService;
